import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchLineObject } from '../../../../mesh/sketch/SketchLineObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenLineCollinearConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenLineCollinearConstraint';

export type { ReferenceDrivenLineCollinearConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenLineCollinearConstraint';

/**
 * 参考驱动直线共线关系定义。
 *
 * 投影直线作为不可编辑的公式载荷参与参数求解，目标线段仍可沿载体直线滑动。
 */
export class ReferenceDrivenLineCollinearConstraint extends SingleTargetRelation<SketchLineObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影直线的世界坐标起点。 */
	private readonly projectedStartWorld: THREE.Vector3;
	/** 投影直线的世界坐标终点。 */
	private readonly projectedEndWorld: THREE.Vector3;
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部直线投影参数。 */
	public constructor(options: ReferenceDrivenLineCollinearConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedStartWorld = options.projectedStartWorld.clone();
		this.projectedEndWorld = options.projectedEndWorld.clone();
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenLineCollinearConstraint {
		return new ReferenceDrivenLineCollinearConstraint({
			referenceId: this.referenceId,
			projectedStartWorld: this.projectedStartWorld.clone(),
			projectedEndWorld: this.projectedEndWorld.clone(),
		});
	}

	/**
	 * 创建统一参数方程使用的局部固定直线载荷。
	 *
	 * @returns 目标草图参考面局部坐标中的外部直线；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		// 使用同一目标参考面转换两个端点，避免跨平面坐标混入方程系统。
		const start = target.referencePlane.worldToLocal(this.projectedStartWorld);
		const end = target.referencePlane.worldToLocal(this.projectedEndWorld);
		return {
			kind: 'external-line',
			referenceId: this.referenceId,
			start: [start.x, start.y],
			end: [end.x, end.y],
		};
	}
}
